import { DistributiveOmit } from '@tanstack/react-query'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { Repository } from '../utils/api-client/api-schemas'
import { AddFindingsDrawer } from './drawers/add-findings-drawer'
import { RepositorySettingsDrawer } from './drawers/repository-settings-drawer'
import { type DrawerContent, WorkflowDrawer } from './drawers/workflow-drawer'
import { type RepositoryActionOverlaysUIState, type RepositoryOverlayType } from './menus/repository-action-menu.store'

export const ManagedWorkflowDrawerInitialModeSchema = z.enum(['create', 'manage'])
export type ManagedWorkflowDrawerInitialMode = z.infer<typeof ManagedWorkflowDrawerInitialModeSchema>

// Re-export types for backward compatibility
export type { RepositoryActionOverlaysUIState, RepositoryOverlayType }

export type RepositoryActionOverlaysProps = {
  repository: DistributiveOmit<Repository, '_links'>
} & RepositoryActionOverlaysUIState

// TODO: Re-use this in repositories-page.tsx.
export const RepositoryActionOverlays = ({
  repository,
  currentOverlay,
  setCurrentOverlay,
}: RepositoryActionOverlaysProps) => {
  return match(currentOverlay)
    .with('add-findings', () => (
      <AddFindingsDrawer repositoryId={repository.id} onCloseDrawer={() => setCurrentOverlay(undefined)} />
    ))
    .with('new-workflow', 'manage-workflows', currentOverlay => (
      <ManagedWorkflowDrawer
        repository={repository}
        initialMode={currentOverlay === 'new-workflow' ? 'create' : 'manage'}
        onCloseDrawer={() => setCurrentOverlay(undefined)}
        setCurrentOverlay={setCurrentOverlay}
      />
    ))
    .with('repository-settings', () => (
      <RepositorySettingsDrawer
        repository={repository}
        onCloseDrawer={() => setCurrentOverlay(undefined)}
        onUploadClick={() => setCurrentOverlay('add-findings')}
      />
    ))
    .with(undefined, () => null)
    .exhaustive()
}

type ManagedWorkflowDrawerProps = {
  repository: DistributiveOmit<Repository, '_links'>
  initialMode: ManagedWorkflowDrawerInitialMode
  onCloseDrawer: () => void
  setCurrentOverlay: (overlay: RepositoryOverlayType | undefined) => void
}

// `edit` is internal drawer state and is not promoted to the overlay store, so
// it cannot be reached from outside this component. `create` and `manage`
// round-trip through the overlay store so external close/reopen preserves the
// user's position.
const ManagedWorkflowDrawer = ({
  repository,
  initialMode,
  onCloseDrawer,
  setCurrentOverlay,
}: ManagedWorkflowDrawerProps) => {
  const [drawerContent, setDrawerContent] = useState<DrawerContent>(initialMode)
  return (
    <WorkflowDrawer
      repositoryId={repository.id}
      repositoryDisplayName={repository.full_name}
      repositoryType={repository.type}
      onCloseDrawer={onCloseDrawer}
      drawerContent={drawerContent}
      onSetDrawerContent={content => {
        setDrawerContent(content)
        match(content)
          .with('create', () => setCurrentOverlay('new-workflow'))
          .with('manage', () => setCurrentOverlay('manage-workflows'))
          .with('edit', () => {})
          .exhaustive()
      }}
    />
  )
}
